/* ============================================================
   GS Damage Protection — Golden Star Jewellery
   Optional per-item 2-year Damage Protection add-on.

   • Pricing: marginal-bracket model (see warrantyPriceCents) computed LIVE
     from the item's own price — never stored per product. Anchors:
     $98→$22.99, $198→$48.99, $998→$138.99, $5,000→$399.99, cap $419.99.
   • Attach: a hidden fixed-price warranty product (handle base
     "damage-protection", split "-2", "-3"… if >100 tiers per product);
     JS picks the variant whose price EXACTLY matches the computed price.
     No exact match → the checkbox does not render (fail-safe).
   • PDP: component is JS-injected under the EComposer price block (the
     ecom section file is machine-generated — do not edit it). Checking on
     the PDP defers the add until the product itself is added to cart.
   • Cart drawer: extends gs-panels.js › GSCart by wrapping the instance's
     render/lineHTML (everything funnels through render, so decoration and
     reconcile run on every cart mutation). No second cart API path.
   • Reconcile: adds pending PDP warranty, removes orphans (protected line
     gone → warranty line goes), syncs warranty qty to the protected line
     (per-unit coverage). One op at a time; failed ops are not retried more
     than twice.
   • Info panel ("What's Covered?") is fully independent of the checkbox
     and stacks ABOVE the cart drawer (z-index 2147483002 > 2147483001).

   Preview mode (until the warranty product exists): append
   ?gs_wty_preview=1 to any URL (persists for the session) — the component
   renders with computed prices but adds nothing to cart.

   Load AFTER gs-panels.js (both defer) — window.GSCartInstance must exist.
   ============================================================ */
(function () {
  'use strict';

  /* -------------------- config -------------------- */
  var cfgEl = document.getElementById('gs-warranty-config');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || cfg.enabled === false) return;

  var HANDLE_BASE = cfg.warrantyHandle || 'damage-protection';
  var ELIGIBLE_TAG = (cfg.eligibleTag || 'warranty-eligible').toLowerCase();
  var PAGE_URL = cfg.pageUrl || '/pages/care-damage-protection';
  var LABEL = cfg.label || 'Add Damage Protection';

  try {
    if (/[?&]gs_wty_preview=1/.test(location.search)) sessionStorage.setItem('gsWtyPreview', '1');
    if (/[?&]gs_wty_preview=0/.test(location.search)) sessionStorage.removeItem('gsWtyPreview');
  } catch (e) {}
  var PREVIEW = false;
  try { PREVIEW = sessionStorage.getItem('gsWtyPreview') === '1'; } catch (e) {}

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ============================================================
     Pricing — Part 1 of the Damage Protection brief. DO NOT change
     bracket rates or the cap without a confirmed Mejuri data point.
     Mejuri-equivalent = marginal brackets → nearest whole dollar − 1¢,
     capped at $421.99. Golden Star = Mejuri − $2.00 (cap → $419.99).
     Monotonic: a pricier item never gets a cheaper warranty.
     ============================================================ */
  function warrantyPriceCents(itemCents) {
    if (!itemCents || itemCents <= 0) return null;
    var dollars = itemCents / 100;
    // Banding (owner-approved 2026-07-11): items ≥ $1,000 priced from the next
    // $100 below — halves the tier count (327→166) while never charging MORE
    // than the exact formula. All confirmed anchors are on-grid and unchanged.
    // MUST stay in sync with warranty-tier-generator.js --band100.
    if (dollars >= 1000) dollars = Math.floor(dollars / 100) * 100;
    var brackets = [[250, 0.2575], [1000, 0.10244], [Infinity, 0.0652]];
    var total = 0, lower = 0;
    for (var i = 0; i < brackets.length; i++) {
      var upper = brackets[i][0], rate = brackets[i][1];
      if (dollars > lower) {
        total += (Math.min(dollars, upper) - lower) * rate;
        lower = upper;
      } else break;
    }
    var mejuriCents = Math.round(total) * 100 - 1;
    if (mejuriCents >= 42199) return 41999;
    var gsCents = mejuriCents - 200;
    return gsCents >= 99 ? gsCents : null;
  }

  /* -------------------- money -------------------- */
  function formatMoney(cents) {
    var inst = window.GSCartInstance;
    if (inst && inst.money) return inst.money(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  /* -------------------- product + warranty catalog -------------------- */
  var productCache = {};
  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  /* Warranty tiers live on one or more hidden products: HANDLE_BASE,
     HANDLE_BASE-2, … (100-variant limit per product). Map price → variant. */
  var catalogPromise = null;
  function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (function walk(suffix, map) {
      var handle = suffix === 1 ? HANDLE_BASE : HANDLE_BASE + '-' + suffix;
      return getProduct(handle).then(function (p) {
        if (!p) return map;
        (p.variants || []).forEach(function (v) {
          if (map[v.price] == null && v.available !== false) map[v.price] = v.id;
        });
        return suffix >= 8 ? map : walk(suffix + 1, map); // sane upper bound
      });
    })(1, {});
    return catalogPromise;
  }
  function resolveWarrantyVariant(itemCents) {
    var price = warrantyPriceCents(itemCents);
    if (price === null) return Promise.resolve(null);
    return loadCatalog().then(function (map) {
      if (map[price]) return { variantId: map[price], priceCents: price };
      if (PREVIEW) return { variantId: null, priceCents: price, preview: true };
      return null; // fail-safe: no exact tier → no checkbox
    });
  }

  function isWarrantyItem(item) {
    if (!item) return false;
    if (item.properties && item.properties._gs_for_key) return true;
    return typeof item.handle === 'string' &&
      (item.handle === HANDLE_BASE || item.handle.indexOf(HANDLE_BASE + '-') === 0);
  }
  function isEligibleProduct(p) {
    if (!p || !p.tags) return false;
    if (typeof p.handle === 'string' &&
      (p.handle === HANDLE_BASE || p.handle.indexOf(HANDLE_BASE + '-') === 0)) return false;
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags).split(',');
    return tags.some(function (t) { return String(t).trim().toLowerCase() === ELIGIBLE_TAG; });
  }

  /* ============================================================
     Component — the two exact states:
       unchecked:  [ ] Add Damage Protection
                       What's Covered? (i)
       checked:    [x] Add Damage Protection
                       CA$138.99          ← revealed, ~0.22s soft animation
                       What's Covered? (i)
     Checkbox and the info control are fully independent.
     ============================================================ */
  var uid = 0;
  function componentHTML(priceCents, checked, context) {
    var id = 'gs-wty-cb-' + (++uid);
    return '' +
      '<div class="gs-wty gs-wty--' + context + (checked ? ' gs-wty--checked' : '') + '" data-gs-wty>' +
        '<div class="gs-wty__row">' +
          '<input type="checkbox" class="gs-wty__input" id="' + id + '" data-gs-wty-input' + (checked ? ' checked' : '') + '>' +
          '<label class="gs-wty__box" for="' + id + '" aria-hidden="true">' +
            '<svg class="gs-wty__check" viewBox="0 0 12 10" fill="none"><path d="M1 5.2 4.2 8.4 11 1.6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</label>' +
          '<div class="gs-wty__text">' +
            '<label class="gs-wty__label" for="' + id + '">' + escapeHtml(LABEL) + '</label>' +
            '<span class="gs-wty__price" data-gs-wty-price aria-live="polite">' +
              (priceCents != null ? formatMoney(priceCents) : '') +
            '</span>' +
            '<button type="button" class="gs-wty__covered" data-gs-wty-info aria-haspopup="dialog" aria-controls="gs-wty-panel">' +
              'What’s Covered?' +
              '<svg class="gs-wty__i" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2"/><rect x="7.3" y="6.8" width="1.4" height="5" rx="0.7" fill="currentColor"/><circle cx="8" cy="4.6" r="0.9" fill="currentColor"/></svg>' +
            '</button>' +
            (PREVIEW ? '<span class="gs-wty__preview-note">PREVIEW — WARRANTY PRODUCT NOT CREATED YET</span>' : '') +
          '</div>' +
        '</div>' +
      '</div>';
  }
  function setChecked(el, checked) {
    el.classList.toggle('gs-wty--checked', !!checked);
    var input = $('[data-gs-wty-input]', el);
    if (input) input.checked = !!checked;
  }

  /* ============================================================
     Info panel — slides in from the right ABOVE the cart drawer.
     Independent of the checkbox; Esc/overlay/X close it. Markup is
     server-rendered in snippets/gs-warranty-panel.liquid.
     ============================================================ */
  var panelRoot = document.getElementById('gs-wty-root');
  var panelEl = document.getElementById('gs-wty-panel');
  var panelOpen = false;
  var panelOpener = null;
  var panelLockedScroll = false;

  function openPanel(opener) {
    if (!panelRoot || panelOpen) return;
    panelOpen = true;
    panelOpener = opener || null;
    panelRoot.classList.add('is-open');
    panelRoot.removeAttribute('hidden');
    panelEl.removeAttribute('inert');
    panelEl.setAttribute('aria-hidden', 'false');
    // Lock page scroll only if the cart drawer hasn't already locked it.
    if (document.body.style.position !== 'fixed') {
      document.body.classList.add('gs-wty-noscroll');
      panelLockedScroll = true;
    }
    var closeBtn = $('[data-gs-wty-close]', panelEl);
    setTimeout(function () { if (closeBtn) { try { closeBtn.focus(); } catch (e) {} } }, 60);
  }
  function closePanel() {
    if (!panelRoot || !panelOpen) return;
    panelOpen = false;
    panelRoot.classList.remove('is-open');
    panelEl.setAttribute('aria-hidden', 'true');
    setTimeout(function () {
      if (!panelOpen) { panelRoot.setAttribute('hidden', ''); panelEl.setAttribute('inert', ''); }
    }, 380);
    if (panelLockedScroll) {
      document.body.classList.remove('gs-wty-noscroll');
      panelLockedScroll = false;
    }
    if (panelOpener && panelOpener.focus) { try { panelOpener.focus(); } catch (e) {} }
    panelOpener = null;
  }
  if (panelRoot) {
    panelRoot.addEventListener('click', function (e) {
      if (e.target.closest('[data-gs-wty-close]') || e.target.hasAttribute('data-gs-wty-scrim')) {
        e.preventDefault();
        closePanel();
      }
    });
    // Capture-phase Esc so the cart drawer (bubble-phase listener) doesn't
    // also close while the info panel is on top of it.
    document.addEventListener('keydown', function (e) {
      if (!panelOpen || e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      closePanel();
    }, true);
  }

  // One delegated handler for every component instance (PDP + cart lines).
  // stopPropagation keeps the info button from ever reaching row handlers —
  // checkbox and panel stay fully independent controls.
  document.addEventListener('click', function (e) {
    var info = e.target.closest('[data-gs-wty-info]');
    if (info) {
      e.preventDefault();
      e.stopPropagation();
      openPanel(info);
    }
  });

  /* ============================================================
     Cart integration — wraps the existing GSCart instance.
     ============================================================ */
  var cartInst = null;
  var prevItems = null;   // snapshot for add-detection diffs
  var reconcileBusy = false;
  var failedOps = {};     // op signature → attempts (never loop a failing op)

  function warrantyLineHTML(item) {
    var money = cartInst.money;
    var forTitle = (item.properties && (item.properties.Protects || item.properties._gs_for_title)) || '';
    return '' +
      '<li class="gs-line-row" data-gs-item>' +
        '<div class="gs-line gs-line--warranty" data-gs-key="' + escapeHtml(item.key) + '">' +
          '<span class="gs-line__media gs-line__media--wty" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none"><path d="M12 2.5 4.5 5.5v6c0 4.6 3.2 8.1 7.5 9.9 4.3-1.8 7.5-5.3 7.5-9.9v-6L12 2.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="m8.7 11.6 2.3 2.3 4.3-4.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</span>' +
          '<div class="gs-line__info">' +
            '<p class="gs-line__title">DAMAGE PROTECTION</p>' +
            (forTitle ? '<p class="gs-line__variant">For: ' + escapeHtml(forTitle) + '</p>' : '') +
            '<div class="gs-line__ctrls">' +
              '<button type="button" class="gs-line__remove" data-gs-remove aria-label="Remove Damage Protection">Remove</button>' +
            '</div>' +
          '</div>' +
          '<div class="gs-line__prices"><p class="gs-line__price">' + money(item.final_line_price) + '</p></div>' +
        '</div>' +
      '</li>';
  }

  function warrantyFor(cart, lineKey) {
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isWarrantyItem(it) && it.properties && it.properties._gs_for_key === lineKey) return it;
    }
    return null;
  }

  function addWarranty(line) {
    return resolveWarrantyVariant(line.final_price).then(function (res) {
      if (!res || !res.variantId) return null;
      var props = {
        _gs_for_key: line.key,
        Protects: (line.product_title || line.title || '') +
          (!line.product_has_only_default_variant && line.variant_title ? ' — ' + line.variant_title : '')
      };
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: res.variantId, quantity: line.quantity || 1, properties: props }] })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; });
        return r.json();
      });
    });
  }

  /* One reconcile op per pass; each op triggers refresh → render → next pass.
     Ops: 1) pending PDP add  2) orphan removal  3) qty sync. */
  function reconcile(cart) {
    if (!cart || reconcileBusy || PREVIEW) return;

    var op = null, opSig = '';

    // 1) pending PDP warranty: the PDP checkbox is checked and the cart diff
    //    shows the selected PDP variant was just added / increased.
    if (pdp.pendingChecked && pdp.currentVariantId) {
      var lineNow = null;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].variant_id === pdp.currentVariantId && !isWarrantyItem(cart.items[i])) { lineNow = cart.items[i]; break; }
      }
      if (lineNow) {
        var prevQty = 0;
        (prevItems || []).forEach(function (it) { if (it.variant_id === lineNow.variant_id && !isWarrantyItem(it)) prevQty += it.quantity; });
        if (lineNow.quantity > prevQty && !warrantyFor(cart, lineNow.key)) {
          op = function () { return addWarranty(lineNow); };
          opSig = 'pdp-add:' + lineNow.key;
        }
      }
    }

    // 2) orphaned warranty lines (their protected line left the cart)
    if (!op) {
      for (var j = 0; j < cart.items.length; j++) {
        var w = cart.items[j];
        if (!isWarrantyItem(w)) continue;
        var forKey = w.properties && w.properties._gs_for_key;
        if (!forKey) continue; // manually added — leave it alone
        var protectedLine = null;
        for (var k = 0; k < cart.items.length; k++) {
          if (cart.items[k].key === forKey) { protectedLine = cart.items[k]; break; }
        }
        if (!protectedLine) {
          op = (function (key) { return function () { return cartInst.change(key, 0); }; })(w.key);
          opSig = 'orphan:' + w.key;
          break;
        }
        // 3) per-unit coverage: warranty qty follows the protected line qty
        if (w.quantity !== protectedLine.quantity) {
          op = (function (key, q) { return function () { return cartInst.change(key, q); }; })(w.key, protectedLine.quantity);
          opSig = 'sync:' + w.key + ':' + protectedLine.quantity;
          break;
        }
      }
    }

    prevItems = cart.items.slice();
    if (!op) return;
    if ((failedOps[opSig] || 0) >= 2) return;

    reconcileBusy = true;
    op().then(function () {
      reconcileBusy = false;
      // pdp.pendingChecked stays true while the box is checked — the qty-diff
      // guard + existing-warranty guard prevent double adds, and a later
      // variant-switch add should also be protected.
      return cartInst.refresh().then(function () { cartInst.publishUpdate(); });
    }).catch(function () {
      reconcileBusy = false;
      failedOps[opSig] = (failedOps[opSig] || 0) + 1;
    });
  }

  /* Inject a checkbox under each eligible, unprotected cart line (and show
     the checked state under protected ones). Runs after every render. */
  function decorateCart(cart) {
    if (!cartInst || !cart) return;
    var linesEl = cartInst.linesEl;
    if (!linesEl) return;

    cart.items.forEach(function (item) {
      if (isWarrantyItem(item)) return;
      var row = $('.gs-line[data-gs-key="' + (window.CSS && CSS.escape ? CSS.escape(item.key) : item.key) + '"]', linesEl);
      if (!row || $('[data-gs-wty]', row)) return;
      var info = $('.gs-line__info', row);
      if (!info) return;

      getProduct(item.handle).then(function (p) {
        if (!isEligibleProduct(p)) return;
        return resolveWarrantyVariant(item.final_price).then(function (res) {
          if (!res) return;
          if ($('[data-gs-wty]', row)) return; // re-render race
          var covered = warrantyFor(cartInst.cart || cart, item.key);
          var holder = document.createElement('div');
          holder.innerHTML = componentHTML(res.priceCents, !!covered, 'cart');
          var el = holder.firstChild;
          info.appendChild(el);

          var input = $('[data-gs-wty-input]', el);
          input.addEventListener('change', function () {
            var current = cartInst.cart || cart;
            var line = null;
            (current.items || []).forEach(function (it) { if (it.key === item.key) line = it; });
            if (!line) return;
            setChecked(el, input.checked);
            input.disabled = true;
            var done = function () {
              input.disabled = false;
              cartInst.refresh().then(function () { cartInst.publishUpdate(); });
            };
            if (PREVIEW) { input.disabled = false; return; }
            if (input.checked) {
              addWarranty(line).then(done).catch(function () { setChecked(el, false); input.disabled = false; });
            } else {
              var w = warrantyFor(current, line.key);
              if (w) cartInst.change(w.key, 0).then(done); else done();
            }
          });
        });
      });
    });
  }

  function patchCart() {
    var inst = window.GSCartInstance;
    if (!inst || inst.__gsWtyPatched) return;
    inst.__gsWtyPatched = true;
    cartInst = inst;

    var origLineHTML = inst.lineHTML.bind(inst);
    inst.lineHTML = function (item) {
      if (isWarrantyItem(item)) return warrantyLineHTML(item);
      return origLineHTML(item);
    };
    var origRender = inst.render.bind(inst);
    inst.render = function (cart) {
      origRender(cart);
      if (cart) {
        decorateCart(cart);
        reconcile(cart);
      }
    };
    // First paint was server-rendered by Liquid — decorate once we have data.
    inst.getCart().then(function (cart) {
      inst.cart = inst.cart || cart;
      prevItems = cart.items.slice();
      decorateCart(cart);
    }).catch(function () {});
  }

  /* ============================================================
     PDP injection — under the EComposer title/price block.
     ============================================================ */
  var pdp = { pendingChecked: false, currentVariantId: null, el: null, product: null };

  function pdpForm() {
    return $('.ecom-product-form--single') ||
      $('form[action*="/cart/add"][data-handle]') ||
      $('form[action*="/cart/add"]');
  }
  function pdpHandle() {
    var form = pdpForm();
    if (form && form.getAttribute('data-handle')) return form.getAttribute('data-handle');
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }
  function pdpSelectedVariant() {
    var form = pdpForm();
    if (!form || !pdp.product) return null;
    var idEl = form.querySelector('[name="id"]');
    var id = idEl ? Number(idEl.value) : NaN;
    var vs = pdp.product.variants || [];
    for (var i = 0; i < vs.length; i++) { if (vs[i].id === id) return vs[i]; }
    return vs[0] || null;
  }
  function pdpAnchor() {
    var price = $('.ecom-product-single__price');
    if (price) { var b = price.closest('.ecom-block'); if (b) return b; }
    var title = $('.ecom-product-single__title');
    if (title) { var t = title.closest('.ecom-block'); if (t) return t; }
    return null;
  }

  function pdpUpdatePrice() {
    if (!pdp.el) return;
    var v = pdpSelectedVariant();
    if (!v) return;
    pdp.currentVariantId = v.id;
    resolveWarrantyVariant(v.price).then(function (res) {
      if (!pdp.el) return;
      if (!res) { // this variant has no tier → hide fail-safe
        pdp.el.style.display = 'none';
        pdp.pendingChecked = false;
        setChecked(pdp.el, false);
        return;
      }
      pdp.el.style.display = '';
      var priceEl = $('[data-gs-wty-price]', pdp.el);
      if (priceEl) priceEl.textContent = formatMoney(res.priceCents);
    });
  }

  function initPDP() {
    if (!/\/products\//.test(location.pathname)) return;
    var handle = pdpHandle();
    if (!handle) return;
    getProduct(handle).then(function (p) {
      if (!isEligibleProduct(p)) return;
      pdp.product = p;
      var v = pdpSelectedVariant();
      if (!v) return;
      resolveWarrantyVariant(v.price).then(function (res) {
        if (!res || pdp.el) return;
        var anchor = pdpAnchor();
        if (!anchor) return;
        pdp.currentVariantId = v.id;
        var holder = document.createElement('div');
        holder.innerHTML = componentHTML(res.priceCents, false, 'pdp');
        pdp.el = holder.firstChild;
        anchor.insertAdjacentElement('afterend', pdp.el);

        var input = $('[data-gs-wty-input]', pdp.el);
        input.addEventListener('change', function () {
          pdp.pendingChecked = input.checked;
          setChecked(pdp.el, input.checked);
        });

        // Variant switches: EComposer updates [name="id"] — recompute on any
        // change inside the form (radios/selects), with a tick for it to land.
        document.addEventListener('change', function (e) {
          var form = pdpForm();
          if (form && (form.contains(e.target) || e.target.name === 'id')) {
            setTimeout(pdpUpdatePrice, 50);
          }
        }, true);
        document.addEventListener('click', function (e) {
          if (e.target.closest('[data-variant-select], .variant-selects, .ecom-product-single__swatch, [data-ecom-swatch]')) {
            setTimeout(pdpUpdatePrice, 120);
          }
        }, true);
      });
    });
  }

  /* -------------------- boot -------------------- */
  function boot() {
    patchCart();
    // Owner decision 2026-07-11: Damage Protection is offered in the CART
    // DRAWER only — no PDP checkbox. Flip showOnProductPage to true in
    // snippets/gs-warranty-panel.liquid to re-enable the PDP placement.
    if (cfg.showOnProductPage === true) initPDP();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-renders the cart section → a fresh GSCart instance.
  document.addEventListener('shopify:section:load', function () { setTimeout(patchCart, 0); });
})();
